import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyTheme,
  readTheme,
  resolveTheme,
  systemPrefersDark,
  writeTheme,
  type ResolvedTheme,
  type Theme,
} from '../lib/theme';
import { TONES, priorityMeta, projectStatusMeta, taskStatusMeta, type Tone } from '../lib/status';

interface ThemeContextValue {
  /** The user's choice */
  theme: Theme;
  /** What is actually on screen */
  resolved: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children, initial }: { children: ReactNode; initial?: Theme }) {
  const [theme, setThemeState] = useState<Theme>(() => initial ?? readTheme());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Follow the OS while the choice is "system".
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const resolved = resolveTheme(theme, systemDark);

  useEffect(() => {
    applyTheme(theme, document.documentElement, systemDark);
  }, [theme, systemDark]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeTheme(next);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

const FALLBACK: ThemeContextValue = {
  theme: 'system',
  resolved: 'light',
  setTheme: () => {
    /* no provider mounted */
  },
};

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? FALLBACK;
}

/** Hex colours for recharts and other inline-styled graphics, matching the current theme. */
export function useChartColors() {
  const { resolved } = useTheme();
  const dark = resolved === 'dark';
  return useMemo(
    () => ({
      dark,
      tone: (tone: Tone) => (dark ? TONES[tone].hexDark : TONES[tone].hex),
      taskStatus: (value: string | null | undefined) => (dark ? TONES[taskStatusMeta(value).tone].hexDark : TONES[taskStatusMeta(value).tone].hex),
      projectStatus: (value: string | null | undefined) => (dark ? TONES[projectStatusMeta(value).tone].hexDark : TONES[projectStatusMeta(value).tone].hex),
      priority: (value: string | null | undefined) => (dark ? TONES[priorityMeta(value).tone].hexDark : TONES[priorityMeta(value).tone].hex),
      grid: dark ? '#374151' : '#e5e7eb',
      axis: dark ? '#9ca3af' : '#6b7280',
      tooltipBg: dark ? '#1f2937' : '#ffffff',
      tooltipBorder: dark ? '#374151' : '#e5e7eb',
      tooltipText: dark ? '#f3f4f6' : '#111827',
    }),
    [dark],
  );
}
